<script lang="ts">
	import { cart } from '$lib/store/cart.store';
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import {
		type CheckoutCreate$input,
		CheckoutCreateStore,
		CheckoutCustomerAssociateStore,
		CheckoutDiscountCodeApplyStore
	} from '$houdini';
	import { languageStore } from '$lib/store/language.store';
	import { isNetworkError, withRetry } from '$lib/utils';
	import { get } from 'svelte/store';
	import { resolveRoute } from '$app/paths';
	import { customer } from '$lib/store/customer.store';
	import { customerSessionStore } from '$lib/store/customerSession.store';
	import { T } from '@tolgee/svelte';
	import { mapToNode } from '$lib/utils/product';
	import { ensureCountry, ensureLocale } from '$lib/utils/i18n';
	import { gtmDispatch } from '$lib/gtm/gtmDispatch';
	import {
		getGtmPayloadCheckoutStarted,
		getGtmPayloadShopifyCheckoutStarted
	} from '$lib/gtm/checkout';

	let retries = 0;
	let dispatchedGtm = false;

	const createCheckout = async () => {
		const checkoutStore = new CheckoutCreateStore();

		let baseInput: CheckoutCreate$input['input'] = {
			lineItems: mapToNode($cart.data?.lines).map((item) => ({
				variantId: item.merchandise.id,
				quantity: item.quantity
			}))
		};

		if ($customer?.defaultAddress) {
			baseInput = {
				...baseInput,
				shippingAddress: {
					address1: $customer.defaultAddress.address1,
					address2: $customer.defaultAddress.address2,
					city: $customer.defaultAddress.city,
					company: $customer.defaultAddress.company,
					country: $customer.defaultAddress.country || $languageStore.country,
					firstName: $customer.defaultAddress.firstName,
					lastName: $customer.defaultAddress.lastName,
					phone: $customer.defaultAddress.phone,
					province: $customer.defaultAddress.province,
					zip: $customer.defaultAddress.zip
				}
			};
		}

		checkoutStore
			.mutate({
				language: ensureLocale(get(languageStore).locale),
				country: ensureCountry(get(languageStore).country),
				input: baseInput
			})
			.then(async ({ data }) => {
				const checkoutId = data?.checkoutCreate?.checkout?.id;
				if (!checkoutId) {
					throw new Error(
						`Checkout ID not found - ${JSON.stringify(data?.checkoutCreate?.checkoutUserErrors)}`
					);
				}

				/*				if (!dispatchedGtm) {
					getGtmPayloadShopifyCheckoutStarted({
						cartId: $cart.data!.id,
						checkoutData: data?.checkoutCreate?.checkout
					}).then((payload) => {
						gtmDispatch(payload);
					});
					dispatchedGtm = true;
				}*/

				if ($customerSessionStore.data?.accessToken) {
					const associationStore = new CheckoutCustomerAssociateStore();
					await withRetry(() =>
						associationStore.mutate({
							checkoutId: checkoutId,
							customerAccessToken: $customerSessionStore.data?.accessToken || '',
							language: ensureLocale(get(languageStore).locale),
							country: ensureCountry(get(languageStore).country)
						})
					);
				}

				if (($cart.data?.discountCodes.length ?? 0) > 0) {
					for (const discountCode of $cart.data?.discountCodes ?? []) {
						if (!discountCode.applicable) {
							continue;
						}
						const store = new CheckoutDiscountCodeApplyStore();
						await withRetry(() =>
							store.mutate({
								checkoutId: checkoutId,
								discountCode: discountCode.code,
								language: ensureLocale(get(languageStore).locale),
								country: ensureCountry(get(languageStore).country)
							})
						);
					}
				}

				const webUrl = data?.checkoutCreate?.checkout?.webUrl;
				if (webUrl) {
					window.location.href = webUrl.toString();
				}
			})
			.catch(async (e) => {
				if (isNetworkError(e)) {
					if (retries < 3) {
						setTimeout(() => retries++, 1000 * (retries + 1));
					}
				} else {
					throw e;
				}
			});
	};

	$: if (browser && $cart.totalQuantity > 0 && $cart.data && retries < 3) {
		createCheckout().then(() => {
			console.log('Redirected!');
		});
	} else if (browser) {
		goto(
			resolveRoute('/[locale]-[country]/cart', {
				...$page.params
			})
		);
	}
</script>

<div class="grid min-h-xl w-full place-items-center">
	<div>
		<div class={'flex items-center justify-center p-10'}>
			<div class="relative w-6 h-6 animate-spin">
				<div class="w-full h-full border-[3px] rounded-full border-dark-background"></div>
				<div
					class="absolute inset-0 w-full h-full border-[3px] rounded-full border-r-dark-primary"
				></div>
			</div>
		</div>
		<T keyName="paymentMethod.redirectionNotice" />
	</div>
</div>
